import * as z from "zod/v4";
import type {
  $ZodArray,
  $ZodDefault,
  $ZodEnum,
  $ZodIntersection,
  $ZodLazy,
  $ZodLiteral,
  $ZodObject,
  $ZodPipe,
  $ZodPromise,
  $ZodRecord,
  $ZodSuccess,
  $ZodTemplateLiteral,
  $ZodTuple,
  $ZodType,
  $ZodTypes,
  $ZodUnion,
} from "zod/v4/core";

/** Thrown when the a given schema type is unsupported */
export class UnsupportedError extends Error {
  constructor(type: $ZodType["_zod"]["def"]["type"]) {
    super(`Cannot generate default value for ${type}`);
  }
}

/** Thrown when the schema is impossible to satisfy */
export class UnresolvableError extends Error {
  constructor(reason: string, extras?: Array<unknown>) {
    super(
      `Canot generate impossible value. Reason: ${reason}`,
      extras
        ? {
            cause: extras,
          }
        : undefined,
    );
  }
}

export const defaultSymbol = Symbol("default");

type WrappedType = Extract<
  $ZodTypes,
  { _zod: { def: { innerType: $ZodType } } }
>;

type TriviallyWrappedType = Exclude<
  WrappedType,
  $ZodPromise | $ZodSuccess | $ZodDefault
>;

function generateTrivialWrappedSchemaValue<
  TSchema extends TriviallyWrappedType,
>(schema: TSchema): TSchema["_zod"]["output"] {
  return generateDefaultValue(schema._zod.def.innerType);
}

function generateDefaultPromise<TSchema extends $ZodPromise>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  return Promise.resolve(generateDefaultValue(schema._zod.def.innerType));
}

function generateDefaultTemplateLiteral<TSchema extends $ZodTemplateLiteral>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const parts: Array<string> = [];
  schema._zod.def.parts.forEach((part) =>
    parts.push(
      part && typeof part === "object" && "_zod" in part
        ? `{${part._zod.pattern.toString()}}`
        : String(part),
    ),
  );

  return parts.join("");
}

function generateDefaultLiteral<TSchema extends $ZodLiteral>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const { values } = schema._zod.def;
  if (values.length == 0) throw new UnresolvableError("emtpy literal");
  return values[0];
}

function generateDefaultPipedValue<TSchema extends $ZodPipe>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  return generateDefaultValue(schema._zod.def.in);
}

function generateDefaultTuple<TSchema extends $ZodTuple>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const { items, rest } = schema._zod.def;
  const values = items.map((item) => generateDefaultValue(item));
  if (rest) values.push(generateDefaultValue(rest));
  return values;
}

function generateDefaultEnum<TSchema extends $ZodEnum>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const entries = Object.entries(schema._zod.def.entries);
  if (entries.length == 0) throw new UnresolvableError("emtpy enum");
  const [, value] = entries[0];
  return value;
}

function generateDefaultUnion<TSchema extends $ZodUnion>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const { options } = schema._zod.def;
  if (options.length == 0) throw new UnresolvableError("emtpy union");
  const errs = [];
  for (const val of options) {
    try {
      return generateDefaultValue(val);
    } catch (e) {
      errs.push(e);
    }
  }
  // throw last error if no option resolved to a value
  throw new UnresolvableError("impossible union", errs);
}

function generateDefaultIntersection<TSchema extends $ZodIntersection>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const { left, right } = schema._zod.def;
  const leftValue = generateDefaultValue(left);
  const rightValue = generateDefaultValue(right);

  try {
    // will make invalid objects, but will match the schema so ¯\_(ツ)_/¯
    return Object.assign(leftValue as {}, rightValue);
  } catch (e) {
    throw new UnresolvableError("impossible intersection", [e]);
  }
}
function generateDefaultArray<TSchema extends $ZodArray>(
  _schema: TSchema,
): TSchema["_zod"]["output"] {
  return [];
}

function generateDefaultObject<TSchema extends $ZodObject>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const newObjectEntries = Object.entries(schema._zod.def.shape).map(
    ([k, v]) => [k, generateDefaultValue(v)],
  );

  return Object.fromEntries(newObjectEntries);
}
function generateDefaultRecord<TSchema extends $ZodRecord>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const { keyType, valueType } = schema._zod.def;
  return {
    [generateDefaultValue(keyType)]: generateDefaultValue(valueType),
  };
}

function getDefaultValue<TSchema extends $ZodDefault>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const { defaultValue } = schema._zod.def;
  return (
    typeof defaultValue === "function" ? defaultValue() : defaultValue
  ) as TSchema["_zod"]["output"];
}

/**
 *
 * @param schema The zod schema
 * @throws {UnresolvableError} when the schema is impossible to satisfy
 * @see {@link UnresolvableError}
 * @throws {UnsupportedError} when a schema type is unsupported (at the time of writing, only `custom`)
 * @see {@link UnsupportedError}
 * @returns a default value
 *
 * ## Recursion Warning:
 * for recursive schemas, the recursion *must not* be the first valid option in a given literal/union/etc
 */
export function generateDefaultValue<TSchema extends $ZodType>(
  schema: TSchema,
): TSchema["_zod"]["output"] {
  const { type } = schema._zod.def;
  switch (type) {
    case "default":
      return getDefaultValue(schema as unknown as $ZodDefault);

    case "lazy":
      return generateDefaultValue(
        (schema as unknown as $ZodLazy)._zod.def.getter(),
      );

    case "nullable":
    case "optional":
    case "nonoptional":
    case "readonly":
    case "catch":
      return generateTrivialWrappedSchemaValue(
        schema as unknown as TriviallyWrappedType,
      );

    case "promise":
      return generateDefaultPromise(schema as unknown as $ZodPromise);
    case "template_literal":
      return generateDefaultTemplateLiteral(
        schema as unknown as $ZodTemplateLiteral,
      );
    case "literal":
      return generateDefaultLiteral(schema as unknown as $ZodLiteral);
    case "pipe":
      return generateDefaultPipedValue(schema as unknown as $ZodPipe);
    case "tuple":
      return generateDefaultTuple(schema as unknown as $ZodTuple);
    case "enum":
      return generateDefaultEnum(schema as unknown as $ZodEnum);
    case "union":
      return generateDefaultUnion(schema as unknown as $ZodUnion);
    case "intersection":
      return generateDefaultIntersection(schema as unknown as $ZodIntersection);
    case "array":
      return generateDefaultArray(schema as unknown as $ZodArray);
    case "record":
      return generateDefaultRecord(schema as unknown as $ZodRecord);
    case "object":
      return generateDefaultObject(schema as unknown as $ZodObject);

    // this utility is mainly intended to create default values for forms,
    // and transform doesn't exactly have an *input type*, so I'm just falling back to string
    case "transform":
    case "string":
      return "";

    case "int":
    case "number":
      return 0;
    case "bigint":
      return BigInt(0);
    case "nan":
      return NaN;
    case "symbol":
      return defaultSymbol;

    case "success":
    case "boolean":
      return true;

    case "void":
    case "undefined":
      return undefined;

    case "null":
      return null;

    case "unknown":
    case "any":
      return {};

    case "date":
      return new Date();
    case "file":
      return new File([], "example");
    case "map":
      return new Map();
    case "set":
      return new Set();

    case "never":
      throw new UnresolvableError("never");
    case "custom":
    default:
      throw new UnsupportedError(type);
  }
}

export function paginatedListSchema<TSchema extends $ZodType>(schema: TSchema) {
  return z.object({
    count: z.number(),
    items: z.array(schema),
    next_page: z.string().nullable(),
    previous_page: z.string().nullable(),
  });
}
